import "./style.scss";

import ActionArrowLeft from "assets/img/svg/actionArrowLeft";
import AgreementValidationModal from "components/AgreementValidationModal";
import StyledButton from "components/shared/StyledButton";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { Body, BodyHighlight, PanelTitle } from "helpers/TypographyComponents";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import type { SignatureProvider } from "store/agreementsStore";

/**
 * Related resource data from agreement modal location state
 */
export type AgreementRelatedResourceData = {
    /**
     * Ressource type
     */
    relatedResource: "term" | "article" | "frameworkContract";
    /**
     * Id of term / article / term_template (for framework contracts)
     */
    relatedResourceId: number;
    beneficiaryId: number;
    /**
     * Publication id (press / AV). Book may omit it on work-only terms.
     * 0 for framework contracts (no title attached).
     */
    titleId?: number | null;
    /**
     * Work id (book). Used to list co-authors on the same work.
     */
    workId?: number | null;
    /**
     * Work name (book), article name (press / AV), or framework contract name
     */
    title: string;
    /**
     * Beneficiary full name
     */
    beneficiary: string;
    /**
     * Beneficiary email — used for Docoon auto-search on press/audiovisual articles
     */
    email?: string;
};

export type NewAgreementLocationState = {
    /**
     * Term data (portal book) / Article data (portal press/audio).
     * Omit when opening the modal from the /agreements list page — the modal will
     * prompt the user to pick a resource as its first step.
     */
    relatedResourceData?: AgreementRelatedResourceData;
    // Optional pre-selection; when omitted, the modal shows an internal step to choose.
    modalType?: "multiple" | "single";
    /**
     * Signature provider — omit for Crealo Sign (default)
     */
    signatureProvider?: SignatureProvider;
    /**
     * Document type to pre-filter templates
     */
    documentType?: "agreement" | "avenant" | "framework_contract";
};

const ContractValidationNew = () => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const { relatedResourceData, modalType, signatureProvider, documentType } =
        (location.state as NewAgreementLocationState | null) ?? {};

    const [showAgreementAndValidationModal, setShowAgreementAndValidationModal] = useState(false);

    const handleOnClose = (hasCreated = false, agreementId?: number) => {
        if (hasCreated && agreementId) {
            setShowAgreementAndValidationModal(false);
            navigate(`/agreements/${agreementId}`);
        } else if (!hasCreated) {
            navigate(-1);
        }
    };

    useEffect(() => {
        setTimeout(() => {
            setShowAgreementAndValidationModal(true);
        }, 1000);
    }, []);

    const ThreeDotMenuOptions: ThreeDotMenuOptions[] = [
        {
            id: 1,
            name: t("ContractValidationEdit.ExportPDF"),
            action: () => {},
        },
        {
            id: 2,
            name: t("ContractValidationEdit.Delete"),
            action: () => {},
        },
    ];

    return (
        <div className="contract-validation-edit-new">
            <div className="contract-validation-edit-left-panel">
                <div className="contract-validation-edit-left-panel-wrapper">
                    <div className="contract-validation-edit-left-panel-top">
                        <StyledButton buttonType="secondary" onClick={() => navigate("/agreements")}>
                            <ActionArrowLeft />
                            {t("ContractValidationEdit.BackToContracts")}
                        </StyledButton>
                    </div>
                    {relatedResourceData && (
                        <div className="contract-validation-edit-left-panel-header">
                            <div>
                                <PanelTitle>
                                    {documentType === "avenant"
                                        ? t("ContractSettings.TypeAvenant")
                                        : documentType === "framework_contract"
                                          ? t("ContractValidation.FrameworkContract")
                                          : t("ContractValidationEdit.Contract")}{" "}
                                    <span style={{ fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}>
                                        {relatedResourceData.title}
                                    </span>{" "}
                                    {t("ContractValidationEdit.For")}{" "}
                                    <span style={{ fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}>
                                        {relatedResourceData.beneficiary}
                                    </span>
                                </PanelTitle>
                                <Body color="grey">{t("ContractValidationEdit.BasedOn")} </Body>
                            </div>
                            <div style={{ display: "flex", gap: "4px" }}>
                                <ThreeDotMenu options={ThreeDotMenuOptions} style={{ borderColor: "black" }} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="contract-validation-edit-right-panel">
                <div className="contract-validation-edit-right-panel-validators">
                    <BodyHighlight color="black">
                        {t(
                            documentType === "avenant"
                                ? "ContractValidationEdit.AvenantValidation"
                                : "ContractValidationEdit.ContractValidation",
                        )}
                    </BodyHighlight>
                </div>
            </div>
            {showAgreementAndValidationModal && (
                <AgreementValidationModal
                    open={showAgreementAndValidationModal}
                    relatedResourceData={relatedResourceData}
                    onClose={handleOnClose}
                    modalType={modalType}
                    signatureProvider={signatureProvider}
                    documentType={documentType}
                />
            )}
        </div>
    );
};

export default ContractValidationNew;
